import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Drawer,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  List as ListIcon,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
  ViewSidebar as ViewSidebarIcon,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { axiosService } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';

// ===== Placeholders: import your existing components =====
import FundingAgencySelector from './FundingAgencySelector';
import ProgramSelector from './ProgramSelector';
import CallsList from './CallsList';
import { markSeen, useNewCounts } from 'shared/hooks/useNewCounts';
import QuickAccessPanel from './QuickAccessPanel';
import LatestCallsCarousel from './LatestCallsCarousel';
import UploadCallDialog from './UploadCallDialog';
import ImportCallsDialog from './ImportCallsDialog';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Button from '@mui/material/Button';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';



/***************************************************
 * OPTION 1: Sidebar Dashboard with Quick Access
 * IMPROVED: Quick Access includes searchable dropdowns and
 *           moderated contribution (create Agency/Program/Call)
 ***************************************************/

/***************************************************
 * Quick-Access Utilities (localStorage persistence)
 ***************************************************/
const LS_KEY = 'funding.quickLinks.v3';

/**
 * A QuickLink can point to a Program or a Call (or custom external URL)
 * type: 'program' | 'call' | 'custom'
 */
function useQuickLinks() {
  const [links, setLinks] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.warn('Failed to parse quick links from LS', e);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(links));
    } catch (e) {
      console.warn('Failed to persist quick links', e);
    }
  }, [links]);

  const add = (link) =>
    setLinks((prev) => {
      // de-duplicate by id+type or href
      const exists = prev.some(
        (l) => (link.id && l.id === link.id && l.type === link.type) || (link.href && l.href === link.href)
      );
      return exists ? prev : [link, ...prev];
    });

  const remove = (idOrHref) =>
    setLinks((prev) => prev.filter((l) => (l.id ? l.id !== idOrHref : l.href !== idOrHref)));

  const update = (idOrHref, patch) =>
    setLinks((prev) =>
      prev.map((l) => {
        const match = l.id ? l.id === idOrHref : l.href === idOrHref;
        return match ? { ...l, ...patch } : l;
      })
    );

  return { links, add, remove, update, setLinks };
}



// ------- API Utils (adjust endpoints to your backend) -------

function normalizeAgency(a = {}) {
  return {
    id:
      String(
        a.id ??
        a.pk ??
        a.public_id ??
        a.slug ??
        (a.name ? a.name.toLowerCase().replace(/\s+/g, '-') : Date.now())
      ),
    name: (a.name ?? a.title ?? '').trim(),
    country: String(
      a.country?.name ?? a.country?.code ?? a.country ?? a.country_code ?? a.countryCode ?? ''
    ).trim(),
    callsCount: Number(a.active_calls_count) || 0,
  };
}

export async function loadAgencies(params = {}) {
  const res = await axiosService.get('/funding/funding-agencies/', { params: { page_size: 1000, ...params } });

  // res is the Axios response; res.data holds your payload
  const payload = res?.data;

  // Accept shapes: array OR {results: [...]} OR {agencies: [...]}
  const raw =
    Array.isArray(payload) ? payload :
    Array.isArray(payload?.results) ? payload.results :
    Array.isArray(payload?.agencies) ? payload.agencies :
    [];

  // Normalize
  const normalized = raw.filter(Boolean).map(normalizeAgency).filter(a => a.name);

  // De-dupe using Map (acts like a value-based "set" keyed by id/name)
  const unique = Array.from(
    new Map(
      normalized.map(a => [a.id || a.name.toLowerCase(), a])
    ).values()
  );

  // Most calls first; A–Z when the counts are equal
  unique.sort((a, b) => b.callsCount - a.callsCount || a.name.localeCompare(b.name));

  return unique;
}



// Requires: either `fetcher` returns payload directly OR you can swap to axiosService.get(...).then(r => r.data)

function normalizeProgram(p = {}, fallbackAgencyId = null) {
  return {
    id: String(
      p.id ??
      p.pk ??
      p.public_id ??
      p.slug ??
      (p.name ? p.name.toLowerCase().replace(/\s+/g, '-') : Date.now())
    ),
    name: (p.name ?? p.title ?? '').trim(),
    agencyId: p.agency ?? p.agency_id ?? fallbackAgencyId ?? null,
    url: p.url || '',
  };
}

export async function loadPrograms(agencyId) {
  // Consistent base (fixes the /api/ vs /funding/ mismatch)
  const url = agencyId
    ? `/funding/funding-programs/?agency=${agencyId}`
    : '/funding/funding-programs/';

  // If your `fetcher` already returns res.data, great; otherwise adapt:
  const payload =  (await axiosService.get(url)).data

  // Accept: array OR { results: [...] } OR { programs: [...] }
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.programs)
    ? payload.programs
    : [];

  // Normalize
  const normalized = raw
    .filter(Boolean)
    .map(p => normalizeProgram(p, agencyId))
    .filter(p => p.name); // keep only named entries

  // De-dupe by id (fallback to lowercased name)
  const unique = Array.from(
    new Map(normalized.map(p => [p.id || p.name.toLowerCase(), p])).values()
  );

  // Sort by name
  unique.sort((a, b) => a.name.localeCompare(b.name));

  return unique;
}


// If your fetcher returns payload directly, keep it.
// Otherwise swap payload line to: const payload = (await axiosService.get(url)).data;

function normalizeCall(c = {}, { agencyId = null, programId = null } = {}) {
  const id =
    c.id ?? c.pk ?? c.public_id ?? c.slug ??
    (c.title ? c.title.toLowerCase().replace(/\s+/g, '-') : Date.now());

  const deadlineRaw = c.deadline ?? c.closing_date ?? c.close_date ?? '';
  // try to normalize to ISO string if parseable; otherwise keep raw
  const deadlineISO = (() => {
    const dt = deadlineRaw ? new Date(deadlineRaw) : null;
    return dt && !isNaN(dt.getTime()) ? dt.toISOString() : (deadlineRaw || '');
  })();

  return {
    id: String(id),
    title: (c.title ?? c.name ?? '').trim(),
    programId: c.program ?? c.program_id ?? programId ?? null,
    agencyId: c.agency ?? c.agency_id ?? agencyId ?? null,
    deadline: deadlineISO, // ISO string or original raw
    url: c.url || '',
    status: c.status || c.state || '', // optional, if backend provides
  };
}

export async function loadCalls({ agencyId, programId } = {}) {


    // only what is set: "agency_id=undefined" asked the server for every call
    const params = new URLSearchParams();
    if (agencyId) params.append('agency_id', agencyId);
    if (programId) params.append('program', programId);

  const url = `/funding/calls/?${params}`;

  const payload = (await axiosService.get(url)).data;

  // Accept: array OR {results: [...]} OR {calls: [...]}
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.calls)
    ? payload.calls
    : [];

  // Normalize & filter empties
  const normalized = raw
    .filter(Boolean)
    .map(c => normalizeCall(c, { agencyId, programId }))
    .filter(c => c.title);

  // De-dupe (Map acts like a value-based set keyed by id/title)
  const unique = Array.from(
    new Map(
      normalized.map(c => [c.id || c.title.toLowerCase(), c])
    ).values()
  );

  // Sort: by deadline (earliest first; empty deadlines last), then by title
  unique.sort((a, b) => {
    const aHas = !!a.deadline, bHas = !!b.deadline;
    if (aHas && bHas) {
      const ad = new Date(a.deadline).getTime();
      const bd = new Date(b.deadline).getTime();
      if (!isNaN(ad) && !isNaN(bd) && ad !== bd) return ad - bd;
    }
    if (aHas !== bHas) return aHas ? -1 : 1;
    return a.title.localeCompare(b.title);
  });

  return unique;
}









/***************************************************
 * Header utilities
 ***************************************************/
const StepPill = ({ icon, label, active }) => (
  <Chip icon={icon} label={label} variant={active ? 'filled' : 'outlined'} color={active ? 'primary' : 'default'} sx={{ mr: 1  }} />
);

const fundingGuideSteps = [
  {
    label: 'Discover',
    title: 'Start with a funding agency',
    description: 'Browse agencies or scan the latest calls to find a relevant opportunity.',
    target: 'workarea',
  },
  {
    label: 'Refine',
    title: 'Choose a programme',
    description: 'Select a programme, or view every call offered by the chosen agency.',
    target: 'breadcrumbs',
  },
  {
    label: 'Compare',
    title: 'Search and filter calls',
    description: 'Use the call list to compare deadlines, categories, and eligibility details.',
    target: 'workarea',
  },
  {
    label: 'Create',
    title: 'Create a call yourself',
    description: 'Use the Create Call button in Quick Access to submit a new funding call directly, without waiting for an import.',
    target: 'createcall',
  },
  {
    label: 'Save time',
    title: 'Keep useful shortcuts close',
    description: 'Pin agencies, programmes, or calls in Quick Access for your next visit.',
    target: 'quickaccess',
  },
];

const GUIDE_DISMISSED_KEY = 'funding.guide.dismissed.v1';
const GUIDE_GAP = 20;
const GUIDE_PADDING = 8;
const GUIDE_EDGE_MARGIN = 16;
const GUIDE_CARD_ESTIMATED_HEIGHT = 190; // used only to decide/anchor placement; actual height is CSS auto
const GUIDE_ACCENT = '#64b5f6';

/**
 * Spotlight tour: dims the page, cuts a static outlined "hole" around the
 * current step's target so it keeps its normal (white) background, and draws
 * an animated arrow from a dark guide card to it. The hole moves smoothly
 * between steps but never pulses/expands. Never blocks interaction with the
 * underlying page — pointer events stay off everywhere except the card's own
 * controls. `onClose(persist)` is called with persist=true when the user should
 * never see the tour auto-open again (finishing it, or "Don't show this again").
 */
function FundingCallsGuideOverlay({ step, onNext, onClose, targetRect }) {
  const guideStep = fundingGuideSteps[step];
  const isLastStep = step === fundingGuideSteps.length - 1;
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const hasTarget = !!targetRect;
  const spot = hasTarget
    ? {
        top: targetRect.top - GUIDE_PADDING,
        left: targetRect.left - GUIDE_PADDING,
        width: targetRect.width + GUIDE_PADDING * 2,
        height: targetRect.height + GUIDE_PADDING * 2,
      }
    : null;

  const cardWidth = Math.min(420, viewport.w - 32);
  const neededSpace = GUIDE_CARD_ESTIMATED_HEIGHT + GUIDE_GAP + GUIDE_EDGE_MARGIN;
  const spaceBelow = hasTarget ? viewport.h - (spot.top + spot.height) : 0;
  const spaceAbove = hasTarget ? spot.top : 0;
  // Prefer hugging the target above/below it; if the target leaves no room on
  // either side (e.g. it spans nearly the full viewport), dock the card to the
  // bottom of the screen instead so it's always fully visible.
  const placement = !hasTarget
    ? 'below'
    : spaceBelow >= neededSpace
    ? 'below'
    : spaceAbove >= neededSpace
    ? 'above'
    : 'docked';
  const placeBelow = placement !== 'above';

  let cardLeft = (viewport.w - cardWidth) / 2;
  let cardTop = null;
  let cardBottom = null;
  let cardEdge = null;
  let targetAnchor = null;

  if (hasTarget) {
    const rawLeft = spot.left + spot.width / 2 - cardWidth / 2;
    cardLeft = Math.min(Math.max(rawLeft, GUIDE_EDGE_MARGIN), viewport.w - cardWidth - GUIDE_EDGE_MARGIN);

    if (placement === 'below') {
      cardTop = spot.top + spot.height + GUIDE_GAP;
      cardEdge = { y: cardTop };
    } else if (placement === 'above') {
      cardBottom = viewport.h - spot.top + GUIDE_GAP;
      cardEdge = { y: spot.top - GUIDE_GAP };
    } else {
      cardBottom = GUIDE_EDGE_MARGIN;
      cardEdge = { y: viewport.h - GUIDE_EDGE_MARGIN - GUIDE_CARD_ESTIMATED_HEIGHT };
    }
    cardEdge.x = Math.min(Math.max(spot.left + spot.width / 2, cardLeft + 24), cardLeft + cardWidth - 24);

    targetAnchor = {
      x: Math.min(Math.max(cardEdge.x, spot.left), spot.left + spot.width),
      y: placeBelow ? spot.top + spot.height : spot.top,
    };
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: (theme) => theme.zIndex.modal + 10, pointerEvents: 'none' }}>
      {/* Dimmed backdrop with a cut-out around the target, so it keeps its normal
          (white) background and reads as "in focus" — static outline, no pulsing */}
      <motion.div
        initial={false}
        animate={spot || { top: viewport.h / 2, left: viewport.w / 2, width: 0, height: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          borderRadius: 14,
          boxShadow: '0 0 0 9999px rgba(8, 11, 20, 0.72)',
          border: spot ? `2px solid ${GUIDE_ACCENT}` : 'none',
        }}
      />

      {/* Arrow connecting the guide card to the target */}
      {spot && targetAnchor && cardEdge && (
        <svg width={viewport.w} height={viewport.h} style={{ position: 'fixed', top: 0, left: 0, overflow: 'visible' }}>
          <defs>
            <marker id="guideArrowHead" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill={GUIDE_ACCENT} />
            </marker>
          </defs>
          <motion.line
            key={`line-${step}`}
            x1={cardEdge.x}
            y1={cardEdge.y}
            x2={targetAnchor.x}
            y2={targetAnchor.y}
            stroke={GUIDE_ACCENT}
            strokeWidth={2.5}
            strokeDasharray="6 6"
            markerEnd="url(#guideArrowHead)"
            initial={{ strokeDashoffset: 24 }}
            animate={{ strokeDashoffset: 0 }}
            transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      )}

      {/* Guide content card — dark background so it reads clearly over the dimmed page */}
      <Box
        component={motion.div}
        key={step}
        initial={{ opacity: 0, y: placeBelow ? -8 : 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        aria-label="Funding Calls guide"
        sx={{
          position: 'fixed',
          left: cardLeft,
          top: cardTop ?? undefined,
          bottom: cardBottom ?? undefined,
          width: cardWidth,
          maxWidth: 'calc(100vw - 32px)',
          pointerEvents: 'auto',
          bgcolor: '#0f172a',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          px: 3,
          py: 2.5,
        }}
      >
        <Typography variant="overline" sx={{ color: GUIDE_ACCENT, fontWeight: 700 }}>
          {guideStep.label} {step + 1} of {fundingGuideSteps.length}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
          {guideStep.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
          {guideStep.description}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2 }}>
          <Button
            size="small"
            onClick={() => onClose(true)}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.75rem', px: 0.5 }}
          >
            Don't show this again
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button size="small" onClick={() => onClose(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Skip</Button>
            <Button size="small" variant="contained" onClick={isLastStep ? () => onClose(true) : onNext}>
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/***************************************************
 * Main Page – Option 1 (Improved)
 ***************************************************/
export default function FundingCallsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // open on page load when something is pinned, closed otherwise; the header button toggles it
  const [quickAccessOpen, setQuickAccessOpen] = useState(() => {
    try { return (JSON.parse(localStorage.getItem(LS_KEY) || '[]') || []).length > 0; } catch (e) { return false; }
  });
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return !localStorage.getItem(GUIDE_DISMISSED_KEY);
    } catch (e) {
      return true;
    }
  });
  const [guideStep, setGuideStep] = useState(0);
  const [guideTargetRect, setGuideTargetRect] = useState(null);
  const guideWorkAreaRef = useRef(null);
  const guideBreadcrumbsRef = useRef(null);
  const guideQuickAccessRef = useRef(null);
  const guideCreateCallRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const handleDragStart = useCallback((e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent) => {
      if (!isDragging.current) return;
      const delta = moveEvent.clientX - dragStartX.current;
      const next = Math.min(520, Math.max(180, dragStartWidth.current + delta));
      setSidebarWidth(next);
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [sidebarWidth]);
  // Page local state – mirrors your original logic
  const [currentStep, setCurrentStep] = useState(0); // 0 agency, 1 program, 2 calls
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [, setSelectedCall] = useState(null);
  const [agencyFilters, setAgencyFilters] = useState({ cooperationType: null, callStatuses: [] });

  // Quick links
  const { links, add, remove } = useQuickLinks();
  const [, setEditorOpen] = useState(false);
  const [, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { user } = useUserContext();
  const isSuperuser = !!(user?.is_superuser || user?.is_staff);

  // Catalog state (provide your real data here or via props/context)
  const [catalog, setCatalog] = useState({ agencies: [], programs: [], calls: [] });
  // the sidebar's number of new calls, as it was on arriving (for the "New calls" card), before it is reset
  const newCounts = useNewCounts();
  const [newCallsForYou] = useState(() => newCounts.funding || 0);
  useEffect(() => { markSeen('funding'); }, []);     // the sidebar's "new calls" chip starts again from now
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [agencyRefreshKey, setAgencyRefreshKey] = useState(0);

  // 
  const navigate = useNavigate();

  // ---- Navigation handlers ----
  // Every call matching search words, across agencies and programs (from the agency search)
  const [keywordView, setKeywordView] = useState(null);       // { keyword } while that list is shown
  const [latestView, setLatestView] = useState(false);        // the latest calls (the "New calls" card)
  const [latestRelevant, setLatestRelevant] = useState(false); // …only those relevant to the person
  const [agencySearch, setAgencySearch] = useState('');      // kept to come back to the same search
  // the "New calls" card: every call added lately, newest first, across all agencies
  const handleShowLatest = (relevant = false) => {
    setLatestRelevant(Boolean(relevant));
    setSelectedAgency(null);
    setSelectedProgram(null);
    setKeywordView(null);
    setAgencyFilters({ cooperationType: null, callStatuses: ['open', 'upcoming'], search: '' });
    setLatestView(true);
    setCurrentStep(2);
  };

  const handleShowMatchingCalls = (keyword, filters = {}) => {
    setLatestView(false);
    setSelectedAgency(null);
    setSelectedProgram(null);
    setAgencyFilters({ cooperationType: filters.cooperationType || null, callStatuses: filters.callStatuses || [], search: keyword });
    setAgencySearch(keyword);
    setKeywordView({ keyword });
    setCurrentStep(2);
  };

  const handleAgencySelect = (agency, filters = {}) => {
    setKeywordView(null);
    setLatestView(false);
    setAgencySearch(filters.search || '');
    setSelectedAgency(agency);
    setSelectedProgram(null);
    setAgencyFilters({ cooperationType: filters.cooperationType || null, callStatuses: filters.callStatuses || [],
      search: filters.search || '' });
    setCurrentStep(1);
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    setCurrentStep(2);
  };

  const handleViewAllCalls = () => {
    setSelectedProgram(null);
    setCurrentStep(2);
  };

  const showGuide = () => {
    setGuideStep(0);
    setGuideOpen(true);
  };

  // persist=true means the tour should never auto-open again (finished, or "Don't show this again")
  const handleGuideClose = (persist) => {
    if (persist) {
      try {
        localStorage.setItem(GUIDE_DISMISSED_KEY, '1');
      } catch (e) {
        // ignore — worst case the tour auto-opens again next visit
      }
    }
    setGuideOpen(false);
  };



  const handleBackToAgencies = () => {
    setKeywordView(null);
    setLatestView(false);
    setCurrentStep(0);
    setSelectedAgency(null);
    setSelectedProgram(null);
  };

  const handleBackToPrograms = () => {
    setCurrentStep(1);
    setSelectedProgram(null);
  };


  const handleCallSelect = (call) => {
    setSelectedCall(call);
    // setCurrentStep(3); // Optional: Update step if needed
    navigate(`/funding/call/${call.id}`, {
      state: {
        selectedAgency,
        selectedProgram,
        selectedCall: call, 
    },
  });
  };

  // Handle contribution submission from QuickAccessPanel
  const handleContributionSubmit = async (payload) => {
    const { kind } = payload;

    try {
      if (kind === 'agency') {
        await axiosService.post('/funding/funding-agencies/', {
          name: payload.name,
          country: payload.country,
          website: payload.website,
          category: payload.category,
        });
        setToast({ severity: 'success', text: 'Agency created successfully!' });
        setAgencyRefreshKey(k => k + 1);
        const agencies = await loadAgencies();
        setCatalog(prev => ({ ...prev, agencies }));
        return;
      }

      if (kind === 'program') {
        let finalAgencyId = payload.agency;

        if (payload.is_new_agency && payload.new_agency_name) {
          const agencyRes = await axiosService.post('/funding/funding-agencies/', {
            name: payload.new_agency_name,
            country: payload.new_agency_country,
            category: payload.new_agency_category || null,
          });
          finalAgencyId = agencyRes.data.id;
          const agencies = await loadAgencies();
          setCatalog(prev => ({ ...prev, agencies }));
        }

        if (!finalAgencyId || finalAgencyId === '__new__') {
          throw new Error('Please select or create an agency');
        }

        await axiosService.post('/funding/funding-programs/', {
          name: payload.name,
          code: payload.code,
          agency: finalAgencyId,
          description: payload.description,
          trl_range: payload.trl_range,
          recurring: payload.recurring,
        });
        setToast({ severity: 'success', text: 'Program created successfully!' });
        const programs = await loadPrograms();
        setCatalog(prev => ({ ...prev, programs }));
        return;
      }

      if (kind === 'call') {
        let finalAgencyId = payload.agency;
        let finalProgramId = payload.program;

        if (payload.is_new_agency && payload.new_agency_name) {
          const agencyRes = await axiosService.post('/funding/funding-agencies/', {
            name: payload.new_agency_name,
            country: payload.new_agency_country,
            category: payload.new_agency_category || null,
          });
          finalAgencyId = agencyRes.data.id;
          const agencies = await loadAgencies();
          setCatalog(prev => ({ ...prev, agencies }));
          setAgencyRefreshKey(k => k + 1);
        }

        if (payload.is_new_program && payload.new_program_name) {
          if (!finalAgencyId || finalAgencyId === '__new__') {
            throw new Error('Please select or create an agency before creating a program');
          }
          const programRes = await axiosService.post('/funding/funding-programs/', {
            name: payload.new_program_name,
            code: payload.code + '-PROG',
            agency: finalAgencyId,
          });
          finalProgramId = programRes.data.id;
          const programs = await loadPrograms();
          setCatalog(prev => ({ ...prev, programs }));
        }

        if (!finalProgramId || finalProgramId === '__new__') {
          throw new Error('Please select or create a program');
        }

        await axiosService.post('/funding/calls/', {
          program: finalProgramId,
          code: payload.code,
          title: payload.title,
          description: payload.description,
          scope: payload.scope,
          expected_outcomes: payload.expected_outcomes,
          eligibility_criteria: payload.eligibility_criteria,
          type_of_action: payload.type_of_action,
          opens_at: payload.opens_at,
          closes_at: payload.closes_at,
          is_always_open: payload.is_always_open,
          timezone: payload.timezone,
          total_budget: payload.total_budget,
          max_per_project: payload.max_per_project,
          min_partners: payload.min_partners,
          submission_url: payload.submission_url,
          external_url: payload.external_url,
          status: payload.status,
        });
        setToast({ severity: 'success', text: 'Call created successfully!' });
        const calls = await loadCalls();
        setCatalog(prev => ({ ...prev, calls }));
        return;
      }
    } catch (e) {
      console.error('Submission error:', e);
      throw e;
    }
  };



  
  // ---- Quick link helpers ----
  // A saved link holds only an id: after a reload the catalog may not (yet) include that agency or
  // program (programs load a page at a time), so fetch it by id when it is not there.
  const findOrFetch = async (list, id, url, normalize) => {
    const hit = list.find((x) => String(x.id) === String(id));
    if (hit) return hit;
    try {
      const { data } = await axiosService.get(url);
      return data ? normalize(data) : null;
    } catch {
      return null;
    }
  };

  const openLink = async (l) => {
    if (l.type === 'call' && l.id) {                  // the call page loads the call from its id
      navigate(`/funding/call/${l.id}`);
      return;
    }
    if (l.type === 'agency' && l.id) {
      const agency = await findOrFetch(catalog.agencies, l.id, `/funding/funding-agencies/${l.id}/`, normalizeAgency);
      if (agency) handleAgencySelect(agency);
      else setToast({ severity: 'warning', text: 'This agency is no longer on Otable.' });
      return;
    }
    if (l.type === 'program' && l.id) {
      const program = await findOrFetch(catalog.programs, l.id, `/funding/funding-programs/${l.id}/`, (p) => normalizeProgram(p));
      if (!program) {
        setToast({ severity: 'warning', text: 'This program is no longer on Otable.' });
        return;
      }
      const agencyId = program.agencyId && typeof program.agencyId === 'object' ? program.agencyId.id : program.agencyId;
      const agency = agencyId
        ? await findOrFetch(catalog.agencies, agencyId, `/funding/funding-agencies/${agencyId}/`, normalizeAgency)
        : null;
      if (agency) setSelectedAgency(agency);
      handleProgramSelect(program);
      return;
    }
    if (l.href) window.open(l.href, '_blank', 'noopener,noreferrer');
  };

  const addAgencyQuickLink = (agency) => {
    add({ 
      id: agency.id, 
      type: 'agency', 
      title: agency.name, 
      subtitle: agency.country || 'Agency' 
    });
    setToast({ severity: 'success', text: 'Agency added to Quick Access' });
  };

  const addProgramQuickLink = (program) => {
    add({ id: program.id, type: 'program', title: program.name, subtitle: selectedAgency?.name || 'Program' });
    setToast({ severity: 'success', text: 'Program added to Quick Access' });
  };

  const addCallQuickLink = (call) => {
    add({ id: call.id, type: 'call', title: call.title, subtitle: selectedProgram?.name || selectedAgency?.name || 'Call' });
    setToast({ severity: 'success', text: 'Call added to Quick Access' });
  };

  const handleRemove = (idOrHref) => {
    remove(idOrHref);
    setToast({ severity: 'info', text: 'Removed from Quick Access' });
  };

  const handleEdit = (l) => {
    setEditing(l);
    setEditorOpen(true);
  };

  const handleAddCustom = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  // Quick Jump actions (trigger your real navigation)
  const jumpToAgency = (agency) => {
    handleAgencySelect(agency);
    // setToast({ severity: 'info', text: `Agency: ${agency.name}` });
  };

  const agencyOf = (id) => (id ? findOrFetch(catalog.agencies, id, `/funding/funding-agencies/${id}/`, normalizeAgency) : null);

  const jumpToProgram = async (program) => {
    const agency = (await agencyOf(program.agencyId)) || selectedAgency;
    if (agency) setSelectedAgency(agency);
    handleProgramSelect(program);
    // setToast({ severity: 'info', text: `Program: ${program.name}` });
  };

  const jumpToCall = async (call) => {
    const prog = catalog.programs.find((p) => p.id === call.programId);
    const agency = await agencyOf(call.agencyId || prog?.agencyId);
    if (agency) setSelectedAgency(agency);
    if (prog) setSelectedProgram(prog);
    handleCallSelect(call);
    // setToast({ severity: 'info', text: `Call: ${call.title}` });
  };

  const stepChips = (
    <Box ref={guideBreadcrumbsRef} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
      <StepPill icon={<BusinessIcon />} label={selectedAgency?.name || (keywordView || latestView ? 'All agencies' : 'Select Agency')} active={currentStep >= 0} />
      <StepPill icon={<CategoryIcon />} label={selectedProgram?.name || (latestView ? 'Latest calls' : keywordView ? `“${keywordView.keyword}”` : 'Choose Program')} active={currentStep >= 1} />
      <StepPill icon={<ListIcon />} label={currentStep === 2 ? 'Browse Calls' : 'Calls'} active={currentStep >= 2} />
    </Box>
  );

  // Track the on-screen position of the current guide step's target element so
  // the spotlight overlay can highlight it and animate smoothly between steps.
  const guideTargetRefsByName = {
    workarea: guideWorkAreaRef,
    breadcrumbs: guideBreadcrumbsRef,
    quickaccess: guideQuickAccessRef,
    createcall: guideCreateCallRef,
  };

  // The Create Call button lives inside the Quick Access panel, so make sure
  // that panel is actually visible before pointing the guide at it.
  useEffect(() => {
    if (!guideOpen) return;
    if (fundingGuideSteps[guideStep]?.target !== 'createcall') return;
    if (isMobile) {
      setSidebarOpen(true);
    } else {
      setQuickAccessOpen(true);
    }
  }, [guideOpen, guideStep, isMobile]);

  useLayoutEffect(() => {
    if (!guideOpen) return undefined;
    const targetName = fundingGuideSteps[guideStep]?.target;
    const el = guideTargetRefsByName[targetName]?.current;

    const measure = () => {
      if (!el) {
        setGuideTargetRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setGuideTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideOpen, guideStep, isMobile, quickAccessOpen, sidebarWidth, currentStep]);

// agencies are not loaded up front (every one of them, the page's slowest request): the selector lists its own,
// and a quick jump fetches the one it needs (findOrFetch)

// initial load: all programs (the quick-access links find theirs here, or fetch it). The calls are not
// loaded up front: nothing here used them, and they were the slowest request of the page

useEffect(() => {
  (async () => {
    setCatalogLoading(true);
    try {
      const programs = await loadPrograms(); // all programs
      setCatalog(prev => ({ ...prev, programs }));
    } catch (e) {
      console.warn('Failed to load programs/calls', e);
    } finally {
      setCatalogLoading(false);
    }
  })();
}, []);

// when an agency is chosen, load its programs (but since we have all, maybe not needed, but keep for filtered)
useEffect(() => {
  (async () => {
    if (!selectedAgency?.id) return;
    try {
      const programs = await loadPrograms(selectedAgency.id);
      setCatalog(prev => ({ ...prev, programs }));
    } catch (e) {
      console.warn('Failed to load programs', e);
    }
  })();
}, [selectedAgency?.id]);

// when a program is chosen (or "view all calls" under an agency), load calls; with neither, nothing (that asked
// for every call on the server, the slowest request of the page)
useEffect(() => {
  if (!selectedAgency?.id && !selectedProgram?.id) return;
  (async () => {
    try {
      const calls = await loadCalls({
        agencyId: selectedAgency?.id || null,
        programId: selectedProgram?.id || null,
      });
      setCatalog(prev => ({ ...prev, calls }));
    } catch (e) {
      console.warn('Failed to load calls', e);
    }
  })();
}, [selectedAgency?.id, selectedProgram?.id]);



  return (
    
    <PageContainer width="wide" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PageHeader title="Global Funding Opportunities" actions={!isMobile && stepChips}>
        <Box display="inline-flex" alignItems="center">
            <Tooltip title="Show Funding Calls guide" placement="bottom">
              <IconButton onClick={showGuide} size="small" sx={{ mr: 0.5 }} aria-label="Show Funding Calls guide">
                <HelpOutlineIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            {isMobile ? (
            <IconButton ref={guideQuickAccessRef} onClick={() => setSidebarOpen(true)} edge="start" sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Tooltip title={quickAccessOpen ? 'Hide Quick Access' : 'Show Quick Access'} placement="bottom">
              <IconButton
                ref={guideQuickAccessRef}
                onClick={() => setQuickAccessOpen(o => !o)}
                size="small"
                sx={{
                  mr: 0.5,
                  bgcolor: quickAccessOpen ? 'primary.main' : 'transparent',
                  color: quickAccessOpen ? '#fff' : 'text.secondary',
                  // border: '5px solid',
                  borderColor: quickAccessOpen ? 'primary.main' : 'divider',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: quickAccessOpen ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ViewSidebarIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
          )}

        </Box>
      </PageHeader>

      {/* Content */}
      {/* overflowX clip (not hidden): no scroll box of its own, so the Quick Access card can stick */}
      <Box sx={{ flex: 1, minHeight: 0, overflowX: 'clip', display: 'flex', alignItems: 'flex-start', mt:1 }}>

        {/* Mobile: Quick Access in Drawer */}
        {isMobile && (
          <Drawer
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{ sx: { width: 340, maxWidth: '90vw' } }}
          >
            <QuickAccessPanel
              links={links}
              onRemove={handleRemove}
              onEdit={handleEdit}
              onOpen={(l) => { openLink(l); setSidebarOpen(false); }}
              onAddCustom={handleAddCustom}
              onJumpAgency={(a) => { jumpToAgency(a); setSidebarOpen(false); }}
              onJumpProgram={(p) => { jumpToProgram(p); setSidebarOpen(false); }}
              onJumpCall={(c) => { jumpToCall(c); setSidebarOpen(false); }}
              contributions={contributions}
              catalog={catalog}
              onContributionSubmit={handleContributionSubmit}
              isSuperuser={isSuperuser}
              onImportCalls={() => setImportDialogOpen(true)}
              createCallButtonRef={guideCreateCallRef}
              
            />
          </Drawer>
        )}

        {/* Desktop: Quick Access, a floating card beside the work area — as tall as its pinned items (up to the
            window), staying in view while the page scrolls; shown only when toggled open */}
        {!isMobile && quickAccessOpen && (
          <>
            <Box
              sx={{
                width: sidebarWidth,
                flexShrink: 0,
                position: 'sticky',
                top: 80,
                maxHeight: 'calc(100vh - 96px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <QuickAccessPanel
                links={links}
                onRemove={handleRemove}
                onEdit={handleEdit}
                onOpen={openLink}
                onAddCustom={handleAddCustom}
                onJumpAgency={jumpToAgency}
                onJumpProgram={jumpToProgram}
                onJumpCall={jumpToCall}
                contributions={contributions}
                catalog={catalog}
                onContributionSubmit={handleContributionSubmit}
                isSuperuser={isSuperuser}
                onImportCalls={() => setImportDialogOpen(true)}
                createCallButtonRef={guideCreateCallRef}
              />
            </Box>

            {/* Drag handle */}
            <Box
              onMouseDown={handleDragStart}
              sx={{
                width: 12,
                flexShrink: 0,
                cursor: 'col-resize',
                alignSelf: 'stretch',
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 0.25,
                '&:hover .drag-line': { bgcolor: 'primary.main' },
              }}
            >
              <Box
                className="drag-line"
                sx={{
                  width: '2px',
                  height: '5%',
                  bgcolor: 'primary.xulight',
                  transition: 'background-color 0.15s',
                }}
              />
            </Box>
          </>
        )}

        {/* Main work area */}
        <Box ref={guideWorkAreaRef} sx={{ flex: 1, minWidth: 0 }}>
            {/* the first step (latest calls, agencies) needs none of the catalogue: it shows at once */}
            {catalogLoading && currentStep !== 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 12 }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Loading funding data...
                </Typography>
              </Box>
            ) : (<>
            {/* STEP 1: Agencies */}
            {currentStep === 0 && (
              <Paper sx={{ p: 2 }}>
                <LatestCallsCarousel onShowLatest={handleShowLatest} newForYou={newCallsForYou} />
                <FundingAgencySelector
                  initialSearch={agencySearch}
                  onShowMatchingCalls={handleShowMatchingCalls}
                  onAgencySelect={handleAgencySelect}
                  selectedAgency={selectedAgency}
                  onCatalog={(data) => setCatalog((prev) => ({ ...prev, agencies: data }))}
                  onPinAgency={addAgencyQuickLink}
                  pinnedIds={links.filter(l => l.type === 'agency').map(l => l.id)}
                  refreshKey={agencyRefreshKey}
                />
              </Paper>
            )}

            {/* STEP 2: Programs */}
            {currentStep === 1 && (
              <Paper sx={{ p: 2 }}>
                <ProgramSelector
                  key={selectedAgency?.id}
                  selectedAgency={selectedAgency}
                  cooperationType={agencyFilters.cooperationType}
                  callStatuses={agencyFilters.callStatuses}
                  initialSearch={agencyFilters.search || ''}
                  onSearchChange={(q) => setAgencyFilters((prev) => (prev.search === q ? prev : { ...prev, search: q }))}
                  onProgramSelect={(program) => {
                    handleProgramSelect(program);
                  }}
                  onViewAllCalls={handleViewAllCalls}
                  onPinProgram={(program) => addProgramQuickLink(program)}
                  onCatalog={(programs) => setCatalog((prev) => ({ ...prev, programs }))}
                  onViewAgencies={handleBackToAgencies}
                  pinnedIds={links.filter(l => l.type === 'program').map(l => l.id)}
                />
              </Paper>
            )}

            {/* STEP 3: Calls */}
            {currentStep === 2 && (
              <Paper sx={{ p: 2 }}>
                <CallsList
                  key={latestView ? `latest:${latestRelevant}` : keywordView ? `q:${keywordView.keyword}` : `a:${selectedAgency?.id}:${selectedProgram?.id}:${agencyFilters.search || ''}`}
                  selectedAgency={selectedAgency}
                  selectedProgram={selectedProgram}
                  latest={latestView}
                  latestRelevant={latestRelevant}
                  onClearLatestRelevant={() => setLatestRelevant(false)}
                  keyword={latestView ? '' : keywordView?.keyword || agencyFilters.search || ''}
                  onClearKeyword={keywordView ? null : () => setAgencyFilters((prev) => ({ ...prev, search: '' }))}
                  cooperationType={agencyFilters.cooperationType}
                  callStatuses={agencyFilters.callStatuses}
                  onBack={keywordView || latestView ? handleBackToAgencies : handleBackToPrograms}
                  onPinCall={(call) => addCallQuickLink(call)}
                  onCatalog={(calls) => setCatalog((prev) => ({ ...prev, calls }))}
                  pinnedIds={links.filter(l => l.type === 'call').map(l => l.id)}
                />
              </Paper>
            )}
            </>)}
        </Box>

      </Box>

      {guideOpen && (
        <FundingCallsGuideOverlay
          step={guideStep}
          targetRect={guideTargetRect}
          onNext={() => setGuideStep((currentGuideStep) => currentGuideStep + 1)}
          onClose={handleGuideClose}
        />
      )}

      {/* Inline Editor for Custom Links */}
      {/* <LinkEditor open={editorOpen} initial={editing} onClose={() => setEditorOpen(false)} onSave={handleSaveEdit} /> */}

      {/* Upload Call Dialog - TODO: Activate only if you have access to LLM AI services */}
      {/* <UploadCallDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSubmit={async (payload) => {
          await handleContributionSubmit(payload);
          setToast({ severity: 'success', text: 'Call created from document!' });
        }}
        catalog={catalog}
      /> */}

      {/* Import Calls Dialog (superuser only) */}
      {isSuperuser && (
        <ImportCallsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImported={() => setToast({ severity: 'success', text: 'Calls imported successfully!' })}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={2200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </PageContainer>
  );
}

/***************************************************
 * WIRING NOTES
 * ------------------------------------------------
 * Use onCatalog callbacks in your selectors/lists to feed the sidebar dropdowns
 * with current Agency/Program/Call inventories.
 *
 * ProgramSelector: expose onPinProgram, onCatalog(programs)
 * CallsList: expose onPinCall, onCatalog(calls)
 *
 * For in-app navigation, store route info in link objects and
 * replace openLink() with navigate(route).
 ***************************************************/
